import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Circle, Line, Rect } from "react-native-svg";

import { Skeleton } from "../../../components/ui";
import { COLORS, FONTS } from "../../../lib/brand";
import { LogoMark } from "../../../components/brand/Logo";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { listMyCompetitions } from "../../../lib/clientCompetitions";
import { useIsLimitedClient } from "../../../lib/clientPermissions";
import { getWelcomeSeenAt } from "../../../lib/clientWelcome";
import type {
  ClientCompetitionListRow,
  CompetitionStatus,
} from "../../../types/database";

const THEME = PANE_THEMES.Client;

const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;
const GOLD = COLORS.GOLD_RM.hex;
const GOLD_TINT = "rgba(201,162,74,0.18)";

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

const CARD_HEIGHT = 220;
const GRID_GAP = 18;
const PAGE_MAX_W = 1240;

// TODO: `companies.logo_url` is the source of truth for per-company branding.
// When that column is empty for a row, we fall back to the Coravu mark below.
// Follow-up: a settings → Your Company → Brand panel for clients to upload
// their own logo (and crop / pad it to the right square ratio).

function statusLabel(s: CompetitionStatus): string {
  return s === "draft" ? "Draft" : s === "open" ? "Open" : s === "judging" ? "Judging" : "Closed";
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * `/client` — top-level Overview Dashboard. Shows every program the client's
 * company owns as a responsive card grid + a "Create program" tile. Licensing
 * cases get a dark forest treatment, Coravu RM cases get a gold treatment.
 */
export default function ClientOverviewDashboardScreen() {
  const { width } = useWindowDimensions();
  const [rows, setRows] = useState<ClientCompetitionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // First-time visitors get bounced to /client/welcome. We hold the dashboard
  // body until this resolves so there's no flash of the grid before the
  // redirect fires. Fails open (treats as "seen") to avoid trap loops.
  const [welcomeChecked, setWelcomeChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const seenAt = await getWelcomeSeenAt();
      if (cancelled) return;
      if (!seenAt) {
        router.replace("/client/welcome" as Href);
        return;
      }
      setWelcomeChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listMyCompetitions();
      setRows(list);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't load programs.";
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  // Effective inner width: viewport minus sidebar (220) when shown.
  const innerWidth = Math.max(0, width - (width >= 900 ? 220 : 0));
  const cols = innerWidth >= 1024 ? 3 : innerWidth >= 640 ? 2 : 1;

  const licensingCount = rows.filter((r) => r.engagement_model === "rm" ? false : true).length;
  const rmCount = rows.filter((r) => r.engagement_model === "rm").length;

  const subtitle = (() => {
    const parts: string[] = [];
    if (licensingCount > 0) parts.push(plural(licensingCount, "self-managed program", "self-managed programs"));
    if (rmCount > 0) parts.push(plural(rmCount, "Coravu-managed program", "Coravu-managed programs"));
    return parts.join(" · ");
  })();

  const isEmpty = !loading && !error && rows.length === 0;
  const { isLimited } = useIsLimitedClient();

  if (!welcomeChecked) {
    // While we resolve the welcome_seen_at flag we render a blank surface so
    // the grid doesn't flash before the redirect to /client/welcome fires.
    return <View style={{ flex: 1, backgroundColor: BLACK }} />;
  }

  if (isEmpty) {
    return <EmptyHero limited={isLimited} />;
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: PAGE_MAX_W }}>
          {/* Hero */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text
              style={{
                fontFamily: FONTS.displaySemiBold,
                fontSize: 10,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Overview
            </Text>
          </View>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.displayLight,
              fontSize: 30,
              lineHeight: 36,
              color: LINEN,
            }}
          >
            Programs
          </Text>
          {subtitle ? (
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.bodyLight,
                fontSize: 13,
                color: WM,
              }}
            >
              {subtitle}
            </Text>
          ) : null}

          {/* Error banner */}
          {error ? (
            <View
              style={{
                marginTop: 18,
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderWidth: 1,
                borderColor: "rgba(220,80,80,0.40)",
                backgroundColor: "rgba(220,80,80,0.08)",
                borderRadius: 4,
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyMedium,
                  fontSize: 12,
                  color: "rgba(255,180,180,0.92)",
                }}
              >
                Couldn't load programs: {error}
              </Text>
            </View>
          ) : null}

          {/* Grid */}
          <View
            style={{
              marginTop: 22,
              flexDirection: "row",
              flexWrap: "wrap",
              gap: GRID_GAP,
            }}
          >
            {loading
              ? [0, 1, 2].map((k) => <ProgramSkeletonCard key={k} cols={cols} />)
              : rows.map((row) => (
                  <ProgramCard
                    key={row.id}
                    row={row}
                    cols={cols}
                    onPress={() => router.push(`/client/competition/${row.id}` as Href)}
                  />
                ))}
            {!loading && !isLimited ? (
              <CreateProgramCard
                cols={cols}
                onPress={() => router.push("/client/competition/new" as Href)}
              />
            ) : null}
          </View>
      </View>
    </ScrollView>
  );
}

/* ─────────────────────────  Cards  ───────────────────────── */

function cardWidthStyle(cols: number): ViewStyle {
  // Use a CSS-grid-friendly basis: each card takes (100% - gaps) / cols.
  const pct = 100 / cols;
  return {
    // RN flex won't honor a calc() string, but react-native-web does. Fall
    // back to a flexBasis percent on native; the gap wrapper handles spacing.
    flexBasis: (`calc(${pct}% - ${(GRID_GAP * (cols - 1)) / cols}px)` as unknown) as number,
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 240,
  };
}

function ProgramCard({
  row,
  cols,
  onPress,
}: {
  row: ClientCompetitionListRow;
  cols: number;
  onPress: () => void;
}) {
  const isRM = row.engagement_model === "rm";
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  const isHover = web && hover;

  const borderColor = isRM
    ? isHover
      ? "rgba(201,162,74,0.55)"
      : "rgba(201,162,74,0.28)"
    : isHover
      ? "rgba(181,135,58,0.30)"
      : BORDER;

  // Surface (gradient on web, flat fallback on native).
  const surfaceStyle: ViewStyle = isRM
    ? ({
        backgroundColor: BLACK,
        ...(web
          ? ({
              backgroundImage:
                "linear-gradient(135deg, rgba(201,162,74,0.05) 0%, rgba(201,162,74,0.12) 100%)",
            } as object)
          : {}),
      } as ViewStyle)
    : ({ backgroundColor: "rgba(28,43,26,0.55)" } as ViewStyle);

  const hoverTint: ViewStyle = isHover
    ? ({
        backgroundColor: isRM ? "rgba(201,162,74,0.06)" : "rgba(181,135,58,0.08)",
      } as ViewStyle)
    : {};

  // TODO: right-click / long-press for a future context menu (rename,
  // duplicate, archive). No-op for now.

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Open ${row.name}`}
      {...(web
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        cardWidthStyle(cols),
        {
          height: CARD_HEIGHT,
          borderRadius: 6,
          borderWidth: 1,
          borderColor,
          overflow: "hidden",
          position: "relative",
        } as ViewStyle,
        surfaceStyle,
        hoverTint,
        web
          ? ({ cursor: "pointer", transition: "border-color 150ms, background-color 150ms" } as object)
          : null,
      ]}
    >
      {/* Watermark */}
      <CardWatermark logoUrl={row.company_logo_url ?? null} opacity={isRM ? 0.10 : 0.06} />

      {/* Content */}
      <View
        style={{
          position: "relative",
          flex: 1,
          paddingHorizontal: 18,
          paddingVertical: 18,
          justifyContent: "space-between",
        }}
      >
        {/* Top: overline pill */}
        <View style={{ flexDirection: "row" }}>
          <View
            style={{
              paddingHorizontal: 9,
              paddingVertical: 3,
              borderRadius: 4,
              backgroundColor: isRM ? GOLD_TINT : "rgba(255,255,255,0.06)",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.displaySemiBold,
                fontSize: 10,
                letterSpacing: 2.4,
                textTransform: "uppercase",
                color: isRM ? FOREST : LINEN,
              }}
            >
              {isRM ? "Coravu Managed" : "Self-Managed"}
            </Text>
          </View>
        </View>

        {/* Middle: case name */}
        <View style={{ flex: 1, justifyContent: "center", paddingVertical: 12 }}>
          <Text
            numberOfLines={2}
            ellipsizeMode="tail"
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 22,
              lineHeight: 26,
              color: LINEN,
            }}
          >
            {isRM ? <Text style={{ color: GOLD }}>{"• "}</Text> : null}
            {row.name}
          </Text>
        </View>

        {/* Bottom row */}
        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
          <StatusPill status={row.status} />
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 11,
              color: WM,
            }}
          >
            {plural(row.registration_count, "candidate", "candidates")}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

function CardWatermark({ logoUrl, opacity }: { logoUrl: string | null; opacity: number }) {
  // Center the watermark and scale to ~70% of card height. Decorative only —
  // pointer-events none so it never intercepts the press.
  const SIZE = Math.round(CARD_HEIGHT * 0.7);
  return (
    <View
      pointerEvents="none"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: "center",
        justifyContent: "center",
        opacity,
      }}
    >
      {logoUrl ? (
        <Image
          source={{ uri: logoUrl }}
          style={{ width: SIZE, height: SIZE }}
          resizeMode="contain"
          accessibilityLabel=""
        />
      ) : (
        <CoravuMark size={SIZE} />
      )}
    </View>
  );
}

function CoravuMark({ size }: { size: number }) {
  // Same glyph as OverviewShell's logo header, scaled up.
  return (
    <LogoMark size={size} tile={FOREST_MID} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
  );
}

function StatusPill({ status }: { status: CompetitionStatus }) {
  return (
    <Text
      style={{
        fontFamily: FONTS.bodyMedium,
        fontSize: 11,
        color: WM,
      }}
    >
      {statusLabel(status)}
    </Text>
  );
}

function CreateProgramCard({ cols, onPress }: { cols: number; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  const isHover = web && hover;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Create a new program"
      {...(web
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        cardWidthStyle(cols),
        {
          height: CARD_HEIGHT,
          borderRadius: 6,
          borderWidth: 1.5,
          borderStyle: "dashed",
          borderColor: isHover ? "rgba(181,135,58,0.55)" : AMBER,
          backgroundColor: isHover ? "rgba(181,135,58,0.06)" : "transparent",
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 24,
        } as ViewStyle,
        web
          ? ({ cursor: "pointer", transition: "border-color 150ms, background-color 150ms" } as object)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 18,
          color: AMBER,
        }}
      >
        + Create program
      </Text>
      <Text
        style={{
          marginTop: 6,
          fontFamily: FONTS.body,
          fontSize: 11,
          color: WM,
          textAlign: "center",
        }}
      >
        New case
      </Text>
    </Pressable>
  );
}

function ProgramSkeletonCard({ cols }: { cols: number }) {
  return (
    <View
      style={[
        cardWidthStyle(cols),
        {
          height: CARD_HEIGHT,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: "rgba(28,43,26,0.40)",
          padding: 18,
          justifyContent: "space-between",
        } as ViewStyle,
      ]}
    >
      <Skeleton width={84} height={16} radius={4} />
      <Skeleton width={"70%" as unknown as number} height={20} radius={4} />
      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <Skeleton width={56} height={12} radius={4} />
        <Skeleton width={80} height={12} radius={4} />
      </View>
    </View>
  );
}

/* ─────────────────────────  Empty state  ───────────────────────── */

function EmptyHero({ limited = false }: { limited?: boolean }) {
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 32,
        paddingVertical: 80,
      }}
    >
      <View style={{ width: "100%", maxWidth: 520 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.displaySemiBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            {limited ? "Programs" : "Welcome"}
          </Text>
        </View>
        <Text
          style={{
            marginTop: 12,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          {limited ? (
            "No assigned programs yet."
          ) : (
            <>
              Post your first{" "}
              <Text style={{ fontStyle: "italic", color: AMBER }}>case.</Text>
            </>
          )}
        </Text>
        <Text
          style={{
            marginTop: 12,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            lineHeight: 21,
            color: WM,
          }}
        >
          {limited
            ? "You'll see programs here once a client assigns you to one. Until then there's nothing to operate."
            : "Two ways to launch a case: Coravu manages the program end-to-end, or you self-manage it on Coravu's rails. You'll choose on the next screen."}
        </Text>
        {!limited ? (
          <Pressable
            onPress={() => router.push("/client/competition/new" as Href)}
            style={[
              {
                alignSelf: "flex-start",
                marginTop: 22,
                paddingHorizontal: 18,
                paddingVertical: 11,
                backgroundColor: AMBER,
                borderRadius: 4,
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11.5,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: FOREST,
              }}
            >
              + Create program
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

void W;
void WS;
void WL;
